import { fetchEntityIfNeeded } from '@classpass/saga-tools';
import { call, select, put, takeEvery } from 'redux-saga/effects';
import { CANCELLATION_LATE } from 'constants/cancellation-types';
import SchedulesDeriver from 'derivers/Schedules';
import { deepExtract } from 'helpers/deep';
import { isEmpty } from 'helpers/verify';
import { Image, RES } from 'models/Image';
import { showModal } from 'redux/modules/modals';
import { userProfile } from 'redux/saga/entities/bound';
import { isLoggedIn } from 'redux/saga/selectors/user';
import shiftTimezone from 'tz-offsets';

const SCHEDULE_ACTION = 'ScheduleCta/SCHEDULE_ACTION';

export const handleScheduleAction = schedule => ({ type: SCHEDULE_ACTION, schedule });

export function* getMissingRequirements(client, schedule) {
  const venueRequirements = Object.entries(deepExtract('venue.requirements.user', schedule, {}))
    .filter(([, required]) => required)
    .map(([requirement]) =>
      requirement
        // Convert identifiers from snake_case to mixedCase
        .replace(/_(\w{1})/g, (_ /* ignored */, character) => character.toUpperCase())
    );

  const profileData = yield call(fetchEntityIfNeeded, userProfile, [client]);
  const missingRequirements = {
    address: !(
      deepExtract('contact_info.address.address1', profileData) && deepExtract('contact_info.address.city', profileData)
    ),
    dateOfBirth: !profileData.date_of_birth,
    phone: !deepExtract('contact_info.phone', profileData),
    emergencyContact: isEmpty(profileData.emergency_contact),
  };

  return venueRequirements.filter(requirement => missingRequirements[requirement]);
}

export function* showCancelReservationModal(client, { schedule }) {
  yield put(
    showModal('CancelReservationModal', {
      cancellationPolicy: schedule.cancellation_policy,
      credits: deepExtract('availability.credits', schedule),
      endTimestamp: shiftTimezone(schedule.endtime, schedule.venue.tz),
      image: Image.getImage(schedule.venue.images, RES.high),
      late: schedule.venue.late_cancellation === CANCELLATION_LATE,
      name: schedule.class.name,
      scheduleId: schedule.id,
      source: schedule.source,
      startTimestamp: shiftTimezone(schedule.starttime, schedule.venue.tz),
      studio: schedule.venue.subtitle ? `${schedule.venue.name} - ${schedule.venue.subtitle}` : schedule.venue.name,
    })
  );
}

export function* doHandleScheduleAction(client, { schedule }) {
  const isUserLoggedIn = yield select(isLoggedIn);

  if (!isUserLoggedIn) {
    return;
  }

  if (SchedulesDeriver.isReserved(schedule)) {
    yield call(showCancelReservationModal, client, { schedule });
    return;
  }

  const missingRequirements = yield call(getMissingRequirements, client, schedule);
  if (!isEmpty(missingRequirements)) {
    yield put(showModal('UpdateProfileModal', { missingRequirements }));
    return;
  }

  const isTopUp = SchedulesDeriver.isTopUp(schedule);
  if (isTopUp) {
    yield put(showModal('ReservationModal', { isTopUp, schedule }));
    return;
  }

  yield put(showModal('ReservationModal', { isTopUp, schedule }));
}

export function* watchScheduleCtaSagas(client) {
  yield takeEvery(handleScheduleAction().type, doHandleScheduleAction, client);
}
